import React, { useMemo } from 'react';
import useSeo from '../hooks/useSeo';
import '../styles/pages/Legal.css';

function RefundPolicy() {
  const seoConfig = useMemo(() => {
    const title = 'BloomNav Refund and Cancellation Policy | Technology Studio';
    const description =
      'How deposits, milestones, retainers, and cancellations work on BloomNav custom software projects.';
    return {
      title,
      description,
      canonicalPath: '/refund',
      ogType: 'article',
      ogImage: '/logo.png',
      ogImageAlt: 'BloomNav'
    };
  }, []);

  useSeo(seoConfig);

  return (
    <div className="legal-page">
      <div className="legal-head">
        <span className="legal-kicker">Legal</span>
        <h1 className="legal-title">
          Refund and <em>cancellation.</em>
        </h1>
        <p className="legal-sub">
          Custom software is billed for time and work already done. Here is how deposits,
          milestones, retainers, and cancellations are handled.
        </p>
      </div>

      <section className="legal-section">
        <h2 className="legal-section-title">1. Scope of this policy</h2>
        <div>
          <p>
            This policy explains when fees paid to CLIVEXCI SYSTEMS LLP trading as BloomNav
            ("BloomNav," "we," "our," or "us") can be refunded. It applies to project work,
            retainers, and support arrangements.
          </p>
          <p>
            If your signed proposal or statement of work sets out different refund terms, that
            document takes precedence for that engagement.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">2. How project billing works</h2>
        <div>
          <p>
            Projects are quoted as a fixed price for an agreed scope, or as an ongoing engagement.
            Payment is usually split across milestones, starting with a deposit that reserves your
            place in our schedule and covers discovery and planning.
          </p>
          <ul className="legal-list">
            <li><strong>Deposit:</strong> reserves capacity and funds scoping, planning, and the first stage of work.</li>
            <li><strong>Milestones:</strong> billed as each agreed stage of the project is delivered.</li>
            <li><strong>Retainers:</strong> billed in advance for the agreed period of ongoing work.</li>
            <li><strong>Third-party costs:</strong> hosting, licences, app store fees, and paid APIs are passed through or paid by you directly.</li>
          </ul>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">3. Deposits</h2>
        <div>
          <p>
            Deposits are non-refundable once work has started. When you book a project we hold
            capacity for you and turn down other work for that period, and discovery and planning
            begin immediately.
          </p>
          <div className="legal-callout">
            <p>
              <strong>Cooling-off:</strong> if you cancel in writing within 7 days of paying the
              deposit and before discovery work has begun, we will refund the deposit in full.
            </p>
          </div>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">4. Work already delivered</h2>
        <div>
          <p>
            Fees for work already carried out are not refundable. This includes discovery, design,
            development, testing, deployment, project management, and support time that has been
            spent, whether or not the wider project continues.
          </p>
          <p>
            If a project ends early, we will invoice or credit against the work completed up to that
            point and any costs already committed on your behalf. Anything you have paid for and we
            have completed is handed over to you.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">5. Cancelling a project</h2>
        <div>
          <h3 className="legal-sub-title">5.1 If you cancel</h3>
          <p>
            You may cancel at any time with written notice. We will stop work, prepare a final
            statement covering completed work and committed costs, and refund any prepaid amount that
            exceeds it. Where a notice period is set out in your agreement, that period is payable.
          </p>

          <h3 className="legal-sub-title">5.2 If we cancel</h3>
          <p>
            If we end an engagement for a reason that is not your fault, we will refund prepaid fees
            for work we have not carried out and hand over everything completed to that point.
          </p>

          <h3 className="legal-sub-title">5.3 Pausing instead</h3>
          <p>
            If your plans change, a pause is often better than a cancellation. We can hold the
            project and pick it up later, subject to availability. Long pauses may need re-planning
            and a revised timeline.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">6. Retainers and support</h2>
        <div>
          <p>
            Retainer and support fees cover a period of availability, so they are not refundable
            once the period has begun. Unused hours do not roll over unless your agreement says so.
          </p>
          <p>
            You can cancel an ongoing arrangement with the notice period in your agreement. Cancelling
            takes effect at the end of the current billing period, and you keep access to agreed
            support until then.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">7. When we will refund</h2>
        <div>
          <p>We will issue a refund in these situations:</p>
          <ul className="legal-list">
            <li><strong>Billing error:</strong> we charged the wrong amount, charged twice, or invoiced for work outside the agreement.</li>
            <li><strong>Work not started:</strong> you cancel within the cooling-off period described above.</li>
            <li><strong>Prepaid and undelivered:</strong> you paid in advance for a stage we did not carry out.</li>
            <li><strong>We cannot deliver:</strong> we are unable to complete work you have paid for and no alternative is agreed.</li>
          </ul>
          <p>
            We will not refund for a change of mind about work already delivered, for delays caused
            by waiting on your feedback or third parties, for outcomes we did not commit to in
            writing, or for problems caused by changes made to the product after handover.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">8. Fixing problems first</h2>
        <div>
          <p>
            If something we delivered does not match the agreed scope, tell us. Defects inside the
            agreed scope are fixed at no cost during the warranty period in your agreement. We would
            always rather correct the work than end the engagement.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">9. How to request a refund</h2>
        <div>
          <p>
            Email us with your company name, the invoice or project reference, the amount, and the
            reason for the request. We will acknowledge within 3 business days and give a decision
            within 10 business days, asking for more detail if we need it.
          </p>
          <p>
            Approved refunds are returned to the original payment method where possible, usually
            within 10 business days of approval. Bank processing times may add to this. Any
            transaction fees charged by payment providers are not refundable.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">10. Disputes</h2>
        <div>
          <p>
            If you disagree with a decision, reply and ask for a review. We will look at it again and
            respond in writing. Both parties agree to try to resolve refund disagreements through
            discussion before starting formal proceedings.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">11. Changes to this policy</h2>
        <div>
          <p>
            We may update this policy from time to time. The date below shows the current version.
            Changes do not affect engagements already agreed under an earlier version.
          </p>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="legal-section-title">12. Contact us</h2>
        <div>
          <p>Billing and refund questions:</p>
          <p><strong>Company:</strong> CLIVEXCI SYSTEMS LLP (BloomNav)</p>
          <p><strong>Email:</strong> support@bloomnav.com</p>
          <p><strong>Subject line:</strong> Refund request</p>
          <p><strong>Location:</strong> Goa, India</p>
        </div>
      </section>

      <div className="legal-stamp">
        <p><strong>Last updated:</strong> July 2026</p>
        <p><strong>Effective date:</strong> July 2026</p>
      </div>
    </div>
  );
}

export default RefundPolicy;
